import React, { useState } from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Grid from "@mui/material/Grid";
import ReactApexChart from "react-apexcharts";

export default function DashboardAccountPayableCounts({
  filter,
  fetchedData,
  PayableCountsList,
}) {
  const legendItems = [
    { label: "Invoice received", color: "#60B564", key: "received" },
    { label: "Invoice processed", color: "#22C3D7", key: "processed" },
    { label: "Invoice in process", color: "#FFA320", key: "inprocess" },
    { label: "Invoice rejected", color: "#D97757", key: "rejected" },
    { label: "Invoice deleted", color: "#EE504D", key: "deleted" },
  ];

  // ✅ Track which legend items are visible (for charts only)
  const [visibleLegends, setVisibleLegends] = useState(
    legendItems.map((item) => item.key)
  );

  // ✅ Toggle chart visibility only (table stays intact)
  const toggleLegend = (key) => {
    setVisibleLegends((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  // ✅ Chart data for PO & Non-PO
  const poData = {
    received: Number(PayableCountsList?.invoicesRecievedPO || 0),
    processed: Number(PayableCountsList?.invoicesProcessedPO || 0),
    inprocess: Number(PayableCountsList?.invoicesInProcessPO || 0),
    rejected: Number(PayableCountsList?.invoicesRejectedPO || 0),
    deleted: Number(PayableCountsList?.invoicesDeletedPO || 0),
  };

  const nonPoData = {
    received: Number(PayableCountsList?.invoicesRecievedNonPO || 0),
    processed: Number(PayableCountsList?.invoicesProcessedNonPO || 0),
    inprocess: Number(PayableCountsList?.invoicesInProcessNonPO || 0),
    rejected: Number(PayableCountsList?.invoicesRejectedNonPO || 0),
    deleted: Number(PayableCountsList?.invoicesDeletedNonPO || 0),
  };
const poPercentageData = {
  inprocess: Number(PayableCountsList?.invoicesInProcessPOPer || 0),
  processed: Number(PayableCountsList?.invoicesProcessedPOPer || 0),
  deleted: Number(PayableCountsList?.invoicesDeletedPOPer || 0),
  rejected: Number(PayableCountsList?.invoicesRejectedPOPer || 0),
};

const nonPoPercentageData = {
  inprocess: Number(PayableCountsList?.invoicesInProcessNonPOPer || 0),
  processed: Number(PayableCountsList?.invoicesProcessedNonPOPer || 0),
  deleted: Number(PayableCountsList?.invoicesDeletedNonPOPer || 0),
  rejected: Number(PayableCountsList?.invoicesRejectedNonPOPer || 0),
};
// Fix the filteredLegendItems for percentage charts
const percentageLegendItems = legendItems.filter(
  (item) =>
    item.key !== "received" // Exclude "received" from percentage charts
);

// Then use this filtered list for percentage series
const poPercentageSeries = percentageLegendItems.map(
  (item) => poPercentageData[item.key]
);

const nonPoPercentageSeries = percentageLegendItems.map(
  (item) => nonPoPercentageData[item.key]
);
const percentageChartOptions = {
  labels: percentageLegendItems.map((item) => item.label),
  colors: percentageLegendItems.map((item) => item.color),
  legend: { show: false },

  dataLabels: {
    enabled: true,
    formatter: (val) => `${Math.round(val)}%`,
    style: {
      fontSize: "11px",
      fontWeight: 600,
      colors: ["#fff"],
    },
  },

  plotOptions: {
    pie: {
      donut: {
        size: "60%",
      },
    },
  },

  tooltip: {
    y: {
      formatter: (val) => `${val}%`,
    },
  },
};;


  // ✅ Filter only for charts (not for table)
  const filteredLegendItems = legendItems.filter(
  (item) => item.key !== "received" && visibleLegends.includes(item.key)
);

  const poChartSeries = filteredLegendItems.map((item) => poData[item.key]);
  const nonPoChartSeries = filteredLegendItems.map(
    (item) => nonPoData[item.key]
  );

  const chartOptions = {
    labels: filteredLegendItems.map((item) => item.label),
    colors: filteredLegendItems.map((item) => item.color),
    legend: { show: false },
    // show value inside each donut slice (smaller font)
   dataLabels: {
  enabled: true,
  formatter: function (val, opts) {
    try {
      const idx = opts.seriesIndex;
      const value = opts.w.config.series[idx];
      return Number(value).toLocaleString();
    } catch (e) {
      return val;
    }
  },
  style: {
    fontSize: "11px",
    fontWeight: 600,
    colors: ["#fff"],
  },
  dropShadow: { enabled: false }
},

plotOptions: {
  pie: {
    donut: {
      size: "60%",    // slightly thicker inner padding (fixes alignment)
      labels: { show: false },
    },
    dataLabels: {
      offset: -1,    // 👈 pulls text inward, perfectly center-aligned
    },
  },
},

    tooltip: {
      y: {
        formatter: (val) => Number(val).toLocaleString(),
      },
    },
  };
  const amountKeyMap = {
  received: { po: "invRecievedPOAmount", nonpo: "invRecievedNonPOAmount" },
  processed: { po: "invProcessedPOAmount", nonpo: "invProcessedNonPOAmount" },
  inprocess: { po: "invInProcessPOAmount", nonpo: "invInProcessNonPOAmount" },
  rejected: { po: "invRejectedPOAmount", nonpo: "invRejectedNonPOAmount" },
  deleted: { po: "invDeletedPOAmount", nonpo: "invDeletedNonPOAmount" },
};
const percentageKeyMap = {
  received: {
    po: "invoiceRecievedPO",
    nonpo: "invoiceRecievedNonPO",
  },
  processed: {
    po: "invoicesProcessedPOPer",
    nonpo: "invoicesProcessedNonPOPer",
  },
  inprocess: {
    po: "invoicesInProcessPOPer",
    nonpo: "invoicesInProcessNonPOPer",
  },
  rejected: {
    po: "invoicesRejectedPOPer",
    nonpo: "invoicesRejectedNonPOPer",
  },
  deleted: {
    po: "invoicesDeletedPOPer",
    nonpo: "invoicesDeletedNonPOPer",
  },
};

const totalData = {
  received: Number(PayableCountsList?.totalRecieved || 0),
  processed: Number(PayableCountsList?.totalInvoicesProcessed || 0),
  inprocess: Number(PayableCountsList?.totalInvoicesInProcess || 0),
  rejected: Number(PayableCountsList?.totalInvoicesRejected || 0),
  deleted: Number(PayableCountsList?.totalInvoicesDeleted || 0),
};

const totalAmountData = {
  received: Number(PayableCountsList?.totalRecievedAmount || 0),
  processed: Number(PayableCountsList?.totalInvProcessedAmount || 0),
  inprocess: Number(PayableCountsList?.totalInvInProcessAmount || 0),
  rejected: Number(PayableCountsList?.totalInvRejectedAmount || 0),
  deleted: Number(PayableCountsList?.totalInvDeletedAmount || 0),
};



  return (
    <div className="payment-summary">
      {/* ✅ Clickable Legend */}
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          gap: "20px",
          marginBottom: "15px",
          flexWrap: "wrap",
        }}
      >
        {legendItems.map((item, i) => {
          const isActive = visibleLegends.includes(item.key);
          return (
            <div
              key={i}
              onClick={() => toggleLegend(item.key)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "5px",
                cursor: "pointer",
                opacity: isActive ? 1 : 0.4,
                transform: isActive ? "scale(1)" : "scale(0.95)",
                transition: "all 0.2s ease",
              }}
            >
              <span
                style={{
                  width: "20px",
                  height: "10px",
                  backgroundColor: item.color,
                  display: "inline-block",
                  borderRadius: "2px",
                }}
              ></span>
              <span style={{ fontSize: "12px", color: "#333" }}>
                {item.label}
              </span>
            </div>
          );
        })}
      </div>

      <Grid container spacing={2}>
        {/* ✅ Table Section (Always full) */}
        <Grid item xs={12} md={12} lg={7} xl={7}>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: "#EFF2F4" }}>
                  <TableCell style={{ color: "black", fontWeight: 600 }}></TableCell>
                  <TableCell style={{ color: "black", fontWeight: 600 }} align="right">
                    PO
                  </TableCell>
                  <TableCell style={{ color: "black", fontWeight: 600 }} align="right">
                    PO Percentage
                  </TableCell>
                  <TableCell style={{ color: "black", fontWeight: 600 }} align="right">
                    Non-PO
                  </TableCell>
                  <TableCell style={{ color: "black", fontWeight: 600 }} align="right">
                    Non-PO Percentage
                  </TableCell>
                  <TableCell style={{ color: "black", fontWeight: 600 }} align="right">
                    PO Amount
                  </TableCell>
                  <TableCell style={{ color: "black", fontWeight: 600 }} align="right">
                    Non-PO Amount
                  </TableCell>
                   <TableCell style={{ color: "black", fontWeight: 600 }} align="right">
                     Total Invoices
                   </TableCell>
                   <TableCell style={{ color: "black", fontWeight: 600 }} align="right">
                    Total Amount
                  </TableCell>
                </TableRow>
              </TableHead>

             <TableBody>
  {legendItems.map((item) => {
    const title =
      item.label.charAt(0).toUpperCase() + item.label.slice(1);
    return (
      <TableRow key={item.key}>
        {/* STATUS */}
        <TableCell   
          style={{
            position: "sticky",
            left: 0,
            backgroundColor: "#fff",
            fontWeight: 500,
            zIndex: 2
          }}
          >
            {title}
            </TableCell>

        {/* PO COUNT */}
        <TableCell align="right">
          {Number(poData[item.key] || 0).toLocaleString()}
        </TableCell>

       {/* PO % */}
<TableCell align="right">
  {`${Number(
    PayableCountsList?.[percentageKeyMap[item.key]?.po] || 0
  ).toLocaleString()}%`}
</TableCell>

        {/* NON-PO COUNT */}
        <TableCell align="right">
          {Number(nonPoData[item.key] || 0).toLocaleString()}
        </TableCell>

     {/* NON-PO % */}
<TableCell align="right">
  {`${Number(
    PayableCountsList?.[percentageKeyMap[item.key]?.nonpo] || 0
  ).toLocaleString()}%`}
</TableCell>

        {/* PO AMOUNT */}
        <TableCell align="right">
          {Number(
            PayableCountsList?.[amountKeyMap[item.key]?.po] || 0
          ).toLocaleString()}
        </TableCell>

        {/* NON-PO AMOUNT */}
        <TableCell align="right">
          {Number(
            PayableCountsList?.[amountKeyMap[item.key]?.nonpo] || 0
          ).toLocaleString()}
        </TableCell>

        {/* TOTAL INVOICES */}
        <TableCell align="right">
          {totalData[item.key].toLocaleString()}
        </TableCell>

        {/* TOTAL AMOUNT */}
        <TableCell align="right">
          {totalAmountData[item.key].toLocaleString()}
        </TableCell>
      </TableRow>
    );
  })}
</TableBody>

            </Table>
          </TableContainer>
        </Grid>

        {/* ✅ Chart Section (Dynamic by legend) */}
        <Grid item xs={12} md={12} lg={5} xl={5}>
          <div
            style={{
              display: "flex",
              gap: "20px",
              flexWrap: "wrap",
              justifyContent: "center",
              padding: "20px 0",
            }}
          >
    {/* PO COUNT */}
    <div style={{ width: "250px" }}>
      <p style={{ fontWeight: "bold", textAlign: "center" }}>PO</p>
      <ReactApexChart
        options={chartOptions}
        series={poChartSeries}
        type="donut"
      />
    </div>

    {/* NON-PO COUNT */}
    <div style={{ width: "250px" }}>
      <p style={{ fontWeight: "bold", textAlign: "center" }}>NON-PO</p>
      <ReactApexChart
        options={chartOptions}
        series={nonPoChartSeries}
        type="donut"
      />
    </div>

            <div style={{ width: "250px" }}>
              <p style={{ fontWeight: "bold", textAlign: "center" }}>PO %</p>
              <ReactApexChart
                options={percentageChartOptions}
                series={poPercentageSeries}
                type="donut"
              />
            </div>

<div style={{ width: "250px" }}>
  <p style={{ fontWeight: "bold", textAlign: "center" }}>NON-PO %</p>
  <ReactApexChart
    options={percentageChartOptions}
    series={nonPoPercentageSeries}
    type="donut"
  />
</div>
  </div>
</Grid>

      </Grid>
    </div>
  );
}    